import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthProvider';
import { teamManagerService } from '../services/teamManagerService';
import LightboxModal from './AdminTeamDetail/LightboxModal';
import PhotoCropModal from './PhotoCropModal';
import RelativeTimePill from './RelativeTimePill';
import { formatRelativeTime } from '../utils/dateUtils';
import { getCountryNameFromCodes, getCountryFlagUrl } from '../data/countries';

// Migration silhouettes were uploaded at exactly 130x130. Real headshots have
// other dimensions. Treating them as "missing" makes filter/sort/stats accurate.
//
// ROLLBACK: flip TREAT_SILHOUETTES_AS_MISSING to false to revert to URL-only
// detection — silhouettes will once again count as "has photo" everywhere.
const TREAT_SILHOUETTES_AS_MISSING = true;

const isPlaceholderImage = (rawData) => {
    const head = rawData?.images?.find(i => i?.imageType === 'PERSON_HEAD');
    if (!head) return false;
    return Number(head.maximumWidth) === 130 && Number(head.maximumHeight) === 130;
};

const hasRealPhoto = (p) => {
    if (!p?.current_photo_url) return false;
    if (TREAT_SILHOUETTES_AS_MISSING && isPlaceholderImage(p?.raw_data)) return false;
    return true;
};

// CSS-only confetti overlay shown briefly when the team manager finishes their roster.
// Pure CSS keyframes + DOM elements — no library, no images.
const Confetti = () => {
    const particles = React.useMemo(() => {
        const colors = ['#fbbf24', '#10b981', '#06b6d4', '#ec4899', '#a855f7', '#ef4444', '#f97316'];
        return Array.from({ length: 80 }, (_, i) => ({
            id: i,
            left: Math.random() * 100,
            delay: Math.random() * 0.6,
            duration: 2.4 + Math.random() * 2.6,
            color: colors[i % colors.length],
            size: 6 + Math.random() * 7,
            rotate: Math.random() * 360,
            shape: i % 3 === 0 ? 'rounded-full' : i % 3 === 1 ? 'rounded-sm' : 'rounded',
        }));
    }, []);

    return (
        <>
            <style>{`
                @keyframes confetti-fall {
                    0%   { transform: translateY(-20px) rotate(0deg); opacity: 1; }
                    100% { transform: translateY(110vh) rotate(720deg); opacity: 0.85; }
                }
            `}</style>
            <div className="fixed inset-0 pointer-events-none z-[60] overflow-hidden" aria-hidden>
                {particles.map(p => (
                    <span
                        key={p.id}
                        className={`absolute ${p.shape}`}
                        style={{
                            left: `${p.left}%`,
                            top: 0,
                            width: `${p.size}px`,
                            height: `${p.size}px`,
                            backgroundColor: p.color,
                            animation: `confetti-fall ${p.duration}s ${p.delay}s ease-in forwards`,
                            transform: `rotate(${p.rotate}deg)`,
                        }}
                    />
                ))}
            </div>
        </>
    );
};

const TeamRosterPage = ({ team }) => {
    const { user, logout } = useAuth();

    const [roster, setRoster] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const [searchQuery, setSearchQuery] = useState('');
    const [filterMode, setFilterMode] = useState('all'); // 'all' | 'missing' | 'has'
    const [genderFilter, setGenderFilter] = useState('all'); // 'all' | 'MALE' | 'FEMALE'
    const [viewMode, setViewMode] = useState(() => {
        try { return localStorage.getItem('teamRoster.viewMode') || 'grid'; }
        catch { return 'grid'; }
    });
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 48;
    const searchInputRef = useRef(null);

    useEffect(() => {
        try { localStorage.setItem('teamRoster.viewMode', viewMode); } catch { /* ignore */ }
    }, [viewMode]);

    const [uploadTarget, setUploadTarget] = useState(null);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadPreview, setUploadPreview] = useState(null);
    const [uploading, setUploading] = useState(false);

    const [lightboxPerson, setLightboxPerson] = useState(null);
    const [celebrating, setCelebrating] = useState(false);
    const [cropImageUrl, setCropImageUrl] = useState(null);

    const codes = useMemo(
        () => [team?.nationality_iso3, team?.nationality_ioc].filter(Boolean),
        [team]
    );

    const countryName = useMemo(
        () => getCountryNameFromCodes(team?.nationality_iso3, team?.nationality_ioc),
        [team]
    );

    const countryFlagUrl = useMemo(
        () => getCountryFlagUrl(team?.nationality_iso3, team?.nationality_ioc, 160),
        [team]
    );

    // The photo's own timestamp lives inside raw_data.images[] for the PERSON_HEAD entry.
    // sr_updated is the *person record's* updated time, which doesn't always change when
    // only the photo is replaced.
    //
    // Sportradar returns timestamps as naive ISO strings (e.g. "2024-07-21T17:37:09") that
    // are actually UTC values. JavaScript parses naive ISO datetimes as LOCAL time, so we
    // append 'Z' to force UTC interpretation. Without this, the relative time is off by
    // the user's timezone offset (e.g., "2h ago" for a just-now upload in UTC+2).
    const normalizeSrTimestamp = (s) => {
        if (!s || typeof s !== 'string') return s;
        // Already has a timezone marker?
        if (/Z$|[+-]\d{2}:?\d{2}$/.test(s)) return s;
        return s + 'Z';
    };
    const getPhotoUpdatedAt = (p) => {
        const imgs = p?.raw_data?.images;
        if (!Array.isArray(imgs) || imgs.length === 0) return null;
        const head = imgs.find(i => i?.imageType === 'PERSON_HEAD') || imgs[0];
        return normalizeSrTimestamp(head?.updated || head?.added || null);
    };

    const fetchRoster = useCallback(async () => {
        try {
            setLoading(true);
            const data = await teamManagerService.getMyRoster();
            setRoster(data);
            return data;
        } catch (err) {
            toast.error(err.message || 'Failed to load roster');
            console.error(err);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchRoster(); }, [fetchRoster]);

    // '/' shortcut to focus search.
    // Skips when user is typing in an input/textarea/contentEditable, or holding modifier keys,
    // or when the upload/lightbox modal is open.
    useEffect(() => {
        const handler = (e) => {
            if (e.key !== '/') return;
            const tag = e.target.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target.isContentEditable) return;
            if (e.metaKey || e.ctrlKey || e.altKey) return;
            if (uploadTarget || lightboxPerson) return;
            e.preventDefault();
            searchInputRef.current?.focus();
            searchInputRef.current?.select();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [uploadTarget, lightboxPerson]);

    const refreshFromSportradar = async () => {
        if (refreshing || codes.length === 0) return;
        setRefreshing(true);
        const toastId = toast.loading('Refreshing roster from Sportradar...');
        try {
            for (const code of codes) {
                await teamManagerService.syncNationality(code);
            }
            toast.dismiss(toastId);
            toast.success('Roster refreshed');
            await fetchRoster();
        } catch (err) {
            toast.dismiss(toastId);
            toast.error(err.message || 'Refresh failed');
            console.error(err);
        } finally {
            setRefreshing(false);
        }
    };

    const openUploadModal = (person) => {
        setUploadTarget(person);
        setUploadFile(null);
        setUploadPreview(null);
    };

    const closeUploadModal = () => {
        if (uploading) return;
        setUploadTarget(null);
        setUploadFile(null);
        if (uploadPreview) URL.revokeObjectURL(uploadPreview);
        setUploadPreview(null);
        if (cropImageUrl) URL.revokeObjectURL(cropImageUrl);
        setCropImageUrl(null);
    };

    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            toast.error('Please choose an image file');
            return;
        }
        // Open the crop modal with the picked file. The cropped blob becomes the
        // uploadFile + preview when the user applies the crop.
        if (cropImageUrl) URL.revokeObjectURL(cropImageUrl);
        setCropImageUrl(URL.createObjectURL(file));
        // Reset the input so the user can re-pick the same file later.
        e.target.value = '';
    };

    const handleCropConfirm = (blob) => {
        if (cropImageUrl) URL.revokeObjectURL(cropImageUrl);
        setCropImageUrl(null);
        if (!blob) return;
        const file = new File([blob], `cropped_${Date.now()}.jpg`, { type: 'image/jpeg' });
        if (uploadPreview) URL.revokeObjectURL(uploadPreview);
        setUploadFile(file);
        setUploadPreview(URL.createObjectURL(blob));
    };

    const handleCropCancel = () => {
        if (cropImageUrl) URL.revokeObjectURL(cropImageUrl);
        setCropImageUrl(null);
        // uploadFile/uploadPreview untouched — user is back at the file picker.
    };

    const handleUploadConfirm = async () => {
        if (!uploadTarget || !uploadFile) return;

        // Capture the current target state BEFORE we overwrite anything; auto-advance only
        // applies when this person was previously missing a photo.
        const wasMissing = !hasRealPhoto(uploadTarget);
        const currentPersonId = uploadTarget.person_id;
        const currentNationality = uploadTarget.nationality;

        setUploading(true);
        const toastId = toast.loading('Uploading to Sportradar...');
        try {
            const photoUrl = await teamManagerService.uploadPhotoForPerson({
                personId: currentPersonId,
                nationality: currentNationality,
                file: uploadFile,
            });
            toast.dismiss(toastId);
            toast.success('Photo uploaded.');

            // Patch the local synced data directly so the new photo shows up immediately.
            // The next full Sportradar sync will overwrite with whatever URL SR ends up using.
            try {
                await teamManagerService.updatePersonPhoto({
                    personId: currentPersonId,
                    newPhotoUrl: photoUrl,
                });
            } catch (patchErr) {
                // Photo upload succeeded; only the local cache patch failed. The next
                // manual Refresh will pull fresh state.
                console.warn('Local cache patch failed (photo was uploaded successfully):', patchErr);
            }

            const freshRoster = await fetchRoster();

            // Auto-advance: only when the just-uploaded person was previously missing.
            // On a re-upload (replace), just close the modal.
            if (!wasMissing) {
                closeUploadModal();
                return;
            }

            const fresh = freshRoster || roster;

            // Auto-advance respects the current filter/search context — otherwise
            // a user searching "Rajani" would jump to an unrelated player after
            // upload. We re-apply the same predicates that produce `visible`.
            const q = searchQuery.trim().toLowerCase();
            const inContext = (p) => {
                if (genderFilter !== 'all' && p.gender !== genderFilter) return false;
                if (filterMode === 'missing' && hasRealPhoto(p)) return false;
                if (filterMode === 'has' && !hasRealPhoto(p)) return false;
                if (q) {
                    return (
                        (p.name_full_latin || '').toLowerCase().includes(q) ||
                        (p.name_full_local || '').toLowerCase().includes(q)
                    );
                }
                return true;
            };

            const next = fresh.find(p =>
                p.person_id !== currentPersonId &&
                !hasRealPhoto(p) &&
                inContext(p)
            );
            if (next) {
                setUploadTarget(next);
                setUploadFile(null);
                if (uploadPreview) URL.revokeObjectURL(uploadPreview);
                setUploadPreview(null);
            } else {
                closeUploadModal();
                // Celebration: this was the last missing photo. Confetti + bigger toast.
                setCelebrating(true);
                setTimeout(() => setCelebrating(false), 4500);
                const total = fresh.filter(hasRealPhoto).length;
                toast.success(
                    total > 0
                        ? `All ${total.toLocaleString()} photos covered. Great work!`
                        : 'All photos covered. Great work!',
                    { duration: 5500, icon: '🎉' }
                );
            }
        } catch (err) {
            toast.dismiss(toastId);
            toast.error(err.message || 'Upload failed');
            console.error(err);
        } finally {
            setUploading(false);
        }
    };

    // Gender filter defines the "team subset". Chip filters (missing/has) and search
    // operate within that subset, and stats reflect the subset (so chip counts stay
    // accurate when the user changes gender).
    const prefiltered = useMemo(() => {
        return roster.filter(p => {
            if (genderFilter !== 'all' && p.gender !== genderFilter) return false;
            return true;
        });
    }, [roster, genderFilter]);

    const stats = useMemo(() => {
        const total = prefiltered.length;
        const withPhoto = prefiltered.filter(r => hasRealPhoto(r)).length;
        return { total, withPhoto, missing: total - withPhoto };
    }, [prefiltered]);

    // Most recent synced_at across the roster — represents when data was last touched
    // by either a bulk sync from Sportradar or a per-person patch (post-upload).
    const lastSyncedAt = useMemo(() => {
        if (!roster.length) return null;
        let max = null;
        for (const r of roster) {
            if (!r.synced_at) continue;
            if (!max || r.synced_at > max) max = r.synced_at;
        }
        return max;
    }, [roster]);

    // Filter + search + sort (missing first, then alphabetical)
    const visible = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        return prefiltered
            .filter(p => {
                if (filterMode === 'missing' && hasRealPhoto(p)) return false;
                if (filterMode === 'has' && !hasRealPhoto(p)) return false;
                if (!q) return true;
                return (
                    (p.name_full_latin || '').toLowerCase().includes(q) ||
                    (p.name_full_local || '').toLowerCase().includes(q)
                );
            })
            .sort((a, b) => {
                const aMissing = !hasRealPhoto(a) ? 0 : 1;
                const bMissing = !hasRealPhoto(b) ? 0 : 1;
                if (aMissing !== bMissing) return aMissing - bMissing;
                return (a.name_full_latin || '').localeCompare(b.name_full_latin || '');
            });
    }, [prefiltered, searchQuery, filterMode]);


    // Reset to page 1 whenever any filter/search changes the result set
    useEffect(() => { setPage(1); }, [searchQuery, filterMode, genderFilter]);

    const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    const pageStart = (safePage - 1) * PAGE_SIZE;
    const pageItems = visible.slice(pageStart, pageStart + PAGE_SIZE);

    const completionPct = stats.total === 0 ? 0 : Math.round((stats.withPhoto / stats.total) * 100);

    return (
        <div className="min-h-screen bg-gray-50">
            <Toaster position="top-right" />

            {/* Header */}
            <header className="bg-mjwc-primary/95 backdrop-blur-xl sticky top-0 z-40 border-b border-white/10 shadow-2xl">
                <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-xl bg-gradient-to-tr from-emerald-400 to-cyan-500 flex items-center justify-center shadow-lg">
                            <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                        </div>
                        <div className="hidden sm:block">
                            <span className="text-white text-xs font-black uppercase tracking-[0.3em] block leading-none">Team</span>
                            <span className="text-emerald-400 text-[9px] font-bold uppercase tracking-[0.2em] block mt-1">Roster</span>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <span className="hidden sm:inline text-white/70 text-xs">{user?.email}</span>
                        <button
                            onClick={refreshFromSportradar}
                            disabled={refreshing}
                            className="px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg text-xs font-bold uppercase tracking-wider transition disabled:opacity-50 flex items-center gap-2"
                            title="Pull the latest persons & photos from Sportradar"
                        >
                            <svg className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            <span className="hidden sm:inline">Refresh</span>
                        </button>
                        <button
                            onClick={logout}
                            className="p-2 hover:bg-white/10 rounded-xl transition-colors text-white/70 hover:text-white"
                            title="Sign out"
                        >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                        </button>
                    </div>
                </div>
            </header>

            {/* Hero strip */}
            <div className="bg-white border-b shadow-sm">
                <div className="max-w-7xl mx-auto px-6 py-6 space-y-5">
                    {/* Title row */}
                    <div className="flex items-end justify-between gap-4 flex-wrap">
                        <div>
                            <h1 className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tight flex items-center gap-3">
                                {countryFlagUrl && (
                                    <img
                                        src={countryFlagUrl}
                                        alt=""
                                        className="w-10 h-7 sm:w-12 sm:h-8 object-cover rounded-md shadow-sm border border-gray-200"
                                        onError={e => { e.currentTarget.style.display = 'none'; }}
                                    />
                                )}
                                <span>{countryName || 'Roster'}</span>
                            </h1>
                            <RelativeTimePill date={lastSyncedAt} label="Last updated" className="mt-2" />
                        </div>
                        <div className="hidden sm:flex flex-col items-end">
                            <div className="text-3xl font-black bg-gradient-to-r from-emerald-500 to-cyan-500 bg-clip-text text-transparent tabular-nums">
                                {completionPct}%
                            </div>
                            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Coverage</div>
                        </div>
                    </div>

                    {/* Stat tiles — order matches the filter pills below (All / Missing / Has photo) */}
                    <div className="grid grid-cols-3 gap-3 sm:gap-4">
                        {/* Total */}
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 sm:p-4 transition hover:shadow-sm">
                            <div className="flex items-center gap-2 mb-1">
                                <span className="w-2 h-2 rounded-full bg-gray-400" />
                                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Total</span>
                            </div>
                            <div className="text-2xl sm:text-3xl font-black text-gray-900 tabular-nums">
                                {stats.total.toLocaleString()}
                            </div>
                            <div className="text-[11px] text-gray-400 mt-0.5">Persons</div>
                        </div>

                        {/* Missing */}
                        <div className="bg-gradient-to-br from-amber-50 to-amber-100/40 border border-amber-200/70 rounded-xl p-3 sm:p-4 transition hover:shadow-sm relative overflow-hidden">
                            <div className="absolute top-0 left-0 bottom-0 w-1 bg-amber-500" />
                            <div className="flex items-center gap-2 mb-1">
                                <span className="w-2 h-2 rounded-full bg-amber-500" />
                                <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700">Missing</span>
                            </div>
                            <div className="text-2xl sm:text-3xl font-black text-amber-700 tabular-nums">
                                {stats.missing.toLocaleString()}
                            </div>
                            <div className="text-[11px] text-amber-600 mt-0.5 tabular-nums">
                                {stats.total > 0 ? `${Math.round((stats.missing / stats.total) * 100)}%` : '0%'} of roster
                            </div>
                        </div>

                        {/* Complete */}
                        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100/40 border border-emerald-200/70 rounded-xl p-3 sm:p-4 transition hover:shadow-sm relative overflow-hidden">
                            <div className="absolute top-0 left-0 bottom-0 w-1 bg-emerald-500" />
                            <div className="flex items-center gap-2 mb-1">
                                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                                <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">Complete</span>
                            </div>
                            <div className="text-2xl sm:text-3xl font-black text-emerald-700 tabular-nums">
                                {stats.withPhoto.toLocaleString()}
                            </div>
                            <div className="text-[11px] text-emerald-600 mt-0.5 tabular-nums">
                                {stats.total > 0 ? `${Math.round((stats.withPhoto / stats.total) * 100)}%` : '0%'} of roster
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
                {/* Sticky filter + search bar with glassmorphism */}
                <div className="sticky top-[60px] z-30 bg-white/60 backdrop-blur-xl border-b border-white/40 shadow-[0_4px_20px_rgba(0,0,0,0.04)] -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 mb-5 transition-all duration-300 overflow-hidden rounded-b-xl space-y-3">
                    {/* Bottom progress strip */}
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gray-200/40">
                        <div
                            className="h-full bg-gradient-to-r from-emerald-400 to-cyan-400 transition-all duration-700 ease-out"
                            style={{ width: `${completionPct}%` }}
                        />
                    </div>

                    {/* Row 1: scope filter (gender) */}
                    <div className="flex flex-wrap gap-2 items-center">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mr-1">Gender</span>
                        <div className="flex bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
                            {[
                                { key: 'all', label: 'All' },
                                { key: 'MALE', label: 'Men' },
                                { key: 'FEMALE', label: 'Women' },
                            ].map((opt, i) => (
                                <button
                                    key={opt.key}
                                    onClick={() => setGenderFilter(opt.key)}
                                    className={`px-3 py-1.5 text-xs font-semibold transition ${i > 0 ? 'border-l border-gray-200' : ''} ${
                                        genderFilter === opt.key
                                            ? 'bg-gray-900 text-white'
                                            : 'bg-white text-gray-600 hover:bg-gray-50 cursor-pointer'
                                    }`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Row 2: chip filters + search + view toggle */}
                    <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                        <div className="flex gap-1.5 bg-white/30 backdrop-blur-md border border-white/30 rounded-xl shadow-sm p-1.5 self-start sm:self-auto">
                            {[
                                { key: 'all', label: 'All', count: stats.total, dot: 'bg-gray-500' },
                                { key: 'missing', label: 'Missing', count: stats.missing, dot: 'bg-amber-500' },
                                { key: 'has', label: 'Has photo', count: stats.withPhoto, dot: 'bg-emerald-500' },
                            ].map(t => {
                                const active = filterMode === t.key;
                                const activeBg =
                                    t.key === 'missing' ? 'bg-amber-500 text-white border-amber-500 shadow-md scale-105'
                                    : t.key === 'has' ? 'bg-emerald-500 text-white border-emerald-500 shadow-md scale-105'
                                    : 'bg-gray-900 text-white border-gray-900 shadow-md scale-105';
                                const inactive = 'bg-white text-gray-700 border-gray-200 cursor-pointer hover:bg-gray-50 hover:border-gray-300 hover:shadow-md hover:-translate-y-0.5';
                                return (
                                    <button
                                        key={t.key}
                                        onClick={() => setFilterMode(t.key)}
                                        className={`px-4 py-2 text-sm rounded-lg font-bold uppercase tracking-wider border transition-all duration-200 flex items-center gap-2 ${
                                            active ? activeBg : inactive
                                        }`}
                                    >
                                        <span className={`w-2 h-2 rounded-full ${active ? 'bg-white/80' : t.dot}`} />
                                        <span>{t.label}</span>
                                        <span className={`text-xs font-semibold ${active ? 'opacity-80' : 'opacity-70 text-gray-500'}`}>
                                            {t.count.toLocaleString()}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                        <div className="relative flex-1">
                            <input
                                ref={searchInputRef}
                                type="text"
                                placeholder="Search player name... (Press '/')"
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                className="w-full px-4 py-2 pl-10 pr-10 bg-white/80 backdrop-blur-md border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white focus:border-indigo-500 transition-all"
                            />
                            <svg className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            {searchQuery && (
                                <button
                                    onClick={() => setSearchQuery('')}
                                    className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                                    aria-label="Clear search"
                                >
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            )}
                        </div>

                        {/* View mode toggle */}
                        <div className="flex bg-white border border-gray-200 rounded-lg shadow-sm self-start sm:self-auto overflow-hidden">
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`px-3 py-2 transition ${viewMode === 'grid' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50 cursor-pointer'}`}
                                title="Grid view"
                                aria-label="Grid view"
                                aria-pressed={viewMode === 'grid'}
                            >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                                </svg>
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`px-3 py-2 transition border-l border-gray-200 ${viewMode === 'list' ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50 cursor-pointer'}`}
                                title="List view"
                                aria-label="List view"
                                aria-pressed={viewMode === 'list'}
                            >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Roster grid */}
                {loading ? (
                    <div className="py-20 text-center text-gray-500">Loading roster...</div>
                ) : visible.length === 0 ? (
                    <div className="py-20 text-center text-gray-500">
                        {roster.length === 0 ? (
                            <>
                                <p className="text-sm">No persons found in your roster yet.</p>
                                <button
                                    onClick={refreshFromSportradar}
                                    className="mt-3 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                                >
                                    Refresh from Sportradar
                                </button>
                            </>
                        ) : (
                            <>
                                <p className="text-sm">No matches.</p>
                                <button
                                    onClick={() => { setSearchQuery(''); setFilterMode('all'); }}
                                    className="mt-2 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                                >
                                    Clear filters
                                </button>
                            </>
                        )}
                    </div>
                ) : (
                    <>
                    {viewMode === 'grid' ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                            {pageItems.map(p => {
                                const real = hasRealPhoto(p);
                                return (
                                <div
                                    key={p.person_id}
                                    className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all p-3 flex gap-3"
                                >
                                    <button
                                        type="button"
                                        onClick={() => real && setLightboxPerson(p)}
                                        disabled={!real}
                                        aria-label={real ? `View photo of ${p.name_full_latin}` : 'No photo to view'}
                                        className={`w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden shrink-0 transition ${
                                            real ? 'cursor-zoom-in hover:ring-2 hover:ring-indigo-300' : 'cursor-default'
                                        }`}
                                    >
                                        {real ? (
                                            // eslint-disable-next-line jsx-a11y/img-redundant-alt
                                            <img
                                                src={p.current_photo_url}
                                                alt={p.name_full_latin || 'photo'}
                                                className="w-full h-full object-cover"
                                                onError={e => { e.currentTarget.style.display = 'none'; }}
                                            />
                                        ) : (
                                            <svg className="w-7 h-7 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                            </svg>
                                        )}
                                    </button>
                                    <div className="flex-1 min-w-0">
                                        <div className="font-semibold text-gray-900 text-sm truncate">
                                            {p.name_full_latin || p.name_full_local || '(unnamed)'}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
                                            {p.dob && <span>{String(p.dob).slice(0, 4)}</span>}
                                            {p.gender && p.dob && <span>·</span>}
                                            {p.gender && <span>{p.gender.toLowerCase()}</span>}
                                        </div>
                                        {real && getPhotoUpdatedAt(p) && (
                                            <div className="text-[10px] text-gray-400 mt-0.5">
                                                Photo updated {formatRelativeTime(getPhotoUpdatedAt(p))}
                                            </div>
                                        )}
                                        <button
                                            onClick={() => openUploadModal(p)}
                                            className={`mt-2 text-xs font-semibold px-2.5 py-1 rounded-md transition ${
                                                real
                                                    ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                                            }`}
                                        >
                                            {real ? 'Replace photo' : 'Upload photo'}
                                        </button>
                                    </div>
                                </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                            {/* Header row (hidden on mobile) */}
                            <div className="hidden md:grid grid-cols-[40px_minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_88px] gap-4 px-4 py-2.5 bg-gray-50 border-b border-gray-200 text-[11px] font-bold uppercase tracking-wider text-gray-500">
                                <span aria-hidden />
                                <span>Name</span>
                                <span>Details</span>
                                <span>Photo updated</span>
                                <span aria-hidden />
                            </div>
                            {pageItems.map(p => {
                                const real = hasRealPhoto(p);
                                return (
                                <div
                                    key={p.person_id}
                                    className="grid grid-cols-[auto_minmax(0,1fr)_auto] md:grid-cols-[40px_minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_88px] gap-3 md:gap-4 px-3 md:px-4 py-2.5 items-center border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition"
                                >
                                    <button
                                        type="button"
                                        onClick={() => real && setLightboxPerson(p)}
                                        disabled={!real}
                                        aria-label={real ? `View photo of ${p.name_full_latin}` : 'No photo to view'}
                                        className={`w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden shrink-0 transition ${
                                            real ? 'cursor-zoom-in hover:ring-2 hover:ring-indigo-300' : 'cursor-default'
                                        }`}
                                    >
                                        {real ? (
                                            // eslint-disable-next-line jsx-a11y/img-redundant-alt
                                            <img
                                                src={p.current_photo_url}
                                                alt={p.name_full_latin || 'photo'}
                                                className="w-full h-full object-cover"
                                                onError={e => { e.currentTarget.style.display = 'none'; }}
                                            />
                                        ) : (
                                            <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                            </svg>
                                        )}
                                    </button>

                                    <div className="min-w-0">
                                        <div className="font-semibold text-gray-900 text-sm truncate">
                                            {p.name_full_latin || p.name_full_local || '(unnamed)'}
                                        </div>
                                        {/* Mobile: meta + photo updated stacked */}
                                        <div className="md:hidden text-xs text-gray-500 mt-0.5 truncate">
                                            {p.dob && <span>{String(p.dob).slice(0, 4)}</span>}
                                            {p.gender && p.dob && <span> · </span>}
                                            {p.gender && <span>{p.gender.toLowerCase()}</span>}
                                        </div>
                                        {real && getPhotoUpdatedAt(p) && (
                                            <div className="md:hidden text-[10px] text-gray-400 mt-0.5">
                                                Photo updated {formatRelativeTime(getPhotoUpdatedAt(p))}
                                            </div>
                                        )}
                                    </div>

                                    {/* Desktop: meta column */}
                                    <div className="hidden md:flex items-center gap-2 text-xs text-gray-500 truncate">
                                        {p.dob && <span>{String(p.dob).slice(0, 4)}</span>}
                                        {p.gender && p.dob && <span>·</span>}
                                        {p.gender && <span>{p.gender.toLowerCase()}</span>}
                                    </div>

                                    {/* Desktop: photo timestamp column */}
                                    <div className="hidden md:block text-xs text-gray-500">
                                        {real && getPhotoUpdatedAt(p)
                                            ? formatRelativeTime(getPhotoUpdatedAt(p))
                                            : <span className="text-gray-300">Never</span>}
                                    </div>

                                    <button
                                        onClick={() => openUploadModal(p)}
                                        className={`w-full text-xs font-semibold px-2 py-1.5 rounded-md transition ${
                                            real
                                                ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                : 'bg-indigo-600 text-white hover:bg-indigo-700'
                                        }`}
                                    >
                                        {real ? 'Replace' : 'Upload'}
                                    </button>
                                </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-6 pt-4 border-t border-gray-200">
                            <p className="text-xs text-gray-500">
                                Showing <span className="font-semibold text-gray-700">{pageStart + 1}</span>–<span className="font-semibold text-gray-700">{Math.min(pageStart + PAGE_SIZE, visible.length)}</span> of <span className="font-semibold text-gray-700">{visible.length.toLocaleString()}</span>
                            </p>
                            <div className="flex items-center gap-1">
                                <button
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={safePage === 1}
                                    className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-lg bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm transition disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    Prev
                                </button>
                                <span className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-gray-700">
                                    Page {safePage} of {totalPages}
                                </span>
                                <button
                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                    disabled={safePage === totalPages}
                                    className="px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-lg bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm transition disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                    </>
                )}
            </main>

            {/* Upload modal */}
            {uploadTarget && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl w-full max-w-md p-6">
                        <h3 className="text-lg font-bold mb-1">
                            {hasRealPhoto(uploadTarget) ? 'Replace' : 'Upload'} photo
                        </h3>
                        <p className="text-sm text-gray-600 mb-4">
                            {uploadTarget.name_full_latin || uploadTarget.name_full_local}
                        </p>

                        <div className="space-y-4">
                            {/* File picker / preview */}
                            {uploadPreview ? (
                                <div className="space-y-2">
                                    <img
                                        src={uploadPreview}
                                        alt="preview"
                                        className="w-32 h-32 object-cover rounded-lg border border-gray-200 mx-auto"
                                    />
                                    <button
                                        onClick={() => {
                                            if (uploadPreview) URL.revokeObjectURL(uploadPreview);
                                            setUploadFile(null);
                                            setUploadPreview(null);
                                        }}
                                        disabled={uploading}
                                        className="w-full text-xs text-gray-500 hover:text-gray-700 disabled:opacity-50"
                                    >
                                        Choose a different file
                                    </button>
                                </div>
                            ) : (
                                <label className="block border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-indigo-400 transition">
                                    <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                    </svg>
                                    <p className="text-sm text-gray-600">Click to choose a photo</p>
                                    <p className="text-xs text-gray-400 mt-1">JPG or PNG. Headshot preferred.</p>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleFileSelect}
                                        className="hidden"
                                    />
                                </label>
                            )}

                            <div className="flex justify-end gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={closeUploadModal}
                                    disabled={uploading}
                                    className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleUploadConfirm}
                                    disabled={uploading || !uploadFile}
                                    className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                                >
                                    {uploading ? 'Uploading...' : 'Upload to Sportradar'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {celebrating && <Confetti />}

            <PhotoCropModal
                show={!!cropImageUrl}
                imageUrl={cropImageUrl}
                onConfirm={handleCropConfirm}
                onCancel={handleCropCancel}
            />

            <LightboxModal
                show={!!lightboxPerson}
                image={lightboxPerson ? {
                    player_name: lightboxPerson.name_full_latin || lightboxPerson.name_full_local || '',
                    photo_url: lightboxPerson.current_photo_url,
                    sportradar_photo_url: lightboxPerson.current_photo_url,
                } : null}
                onClose={() => setLightboxPerson(null)}
            />
        </div>
    );
};

export default TeamRosterPage;
